import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { checkIsAdmin, poserCodeAdmin } from '../../../services/adminApi';
import AdminIcon from './AdminIcon';
import useAdminStatus from './useAdminStatus';
import {
  ADMIN_BASE, ADMIN_GROUPS, DEFAULT_ADMIN_KEY, adminKeyFromPathname, adminPath, lireDerniereSection, memoriserDerniereSection
} from './adminNav';
import './admin-shell.css';

// COQUE DE L'ADMIN (refonte, phase 1) : menu a gauche en 4 groupes, en-tete avec recherche et pastille de mode permanente,
// une route par section. Elle garde l'acces EXACTEMENT comme l'ancienne page : le serveur tranche route par route ; ici on
// ne decide que de ce qu'on affiche (404 pour qui n'y a pas droit, champ de code quand un code partage est configure).

function Gate({ children }) {
  const { t } = useTranslation('admin');
  const [autorise, setAutorise] = useState(null); // null = verification en cours
  const [codeAttendu, setCodeAttendu] = useState(false);
  const [code, setCode] = useState('');
  const [codeRefuse, setCodeRefuse] = useState(false);
  const [connecte, setConnecte] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const verifier = useCallback(async () => {
    const { isAdmin, codeAttendu: attendu, connecte: aSession = true } = await checkIsAdmin();
    setAutorise(isAdmin);
    setCodeAttendu(attendu);
    setConnecte(aSession);
    return isAdmin;
  }, []);

  // Sans connexion, on renvoie a la connexion puis ici (returnTo, lu par LoginLuxe).
  const seConnecter = () => {
    try { localStorage.setItem('returnTo', location.pathname + location.search); } catch (_e) { /* sans stockage : retour au tableau de bord */ }
    navigate('/login');
  };

  useEffect(() => { verifier(); }, [verifier]);

  const soumettre = async (evenement) => {
    evenement.preventDefault();
    setCodeRefuse(false);
    poserCodeAdmin(code.trim());
    const ouvert = await verifier();
    if (!ouvert) {
      // Code faux : on l'efface, sinon toutes les requetes suivantes partiraient avec lui et compteraient dans la limite de debit.
      poserCodeAdmin('');
      setCodeRefuse(true);
    }
  };

  if (autorise === true) return children(autorise);

  return (
    <div className="adm adm-gate">
      {autorise === null && <p className="adm-state" role="status">{t('gate.checking')}</p>}
      {autorise === false && !connecte && (
        <>
          <p className="adm-state">{t('gate.loginFirst')}</p>
          <button type="button" className="btn btn-primary" onClick={seConnecter}>{t('gate.login')}</button>
        </>
      )}
      {autorise === false && connecte && !codeAttendu && (
        <>
          <p className="adm-state">{t('gate.notFound')}</p>
          <Link className="btn btn-outline" to="/dashboard">{t('gate.backToDashboard')}</Link>
        </>
      )}
      {autorise === false && connecte && codeAttendu && (
        <form className="adm-gate-form" onSubmit={soumettre}>
          <h1>{t('gate.title')}</h1>
          <p className="adm-note">{t('gate.codeNote')}</p>
          <label className="adm-sr" htmlFor="adm-code">{t('gate.codePlaceholder')}</label>
          <input
            id="adm-code"
            className="adm-input"
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('gate.codePlaceholder')}
            autoComplete="off"
          />
          {codeRefuse && <p className="adm-error" role="alert">{t('gate.codeRefused')}</p>}
          <button className="btn btn-primary" type="submit" disabled={!code.trim()}>{t('gate.open')}</button>
          <Link className="btn btn-outline" to="/dashboard">{t('gate.backToDashboard')}</Link>
        </form>
      )}
    </div>
  );
}

function Shell() {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, alertes, marquerTraficVu } = useAdminStatus(true);
  const [menuOuvert, setMenuOuvert] = useState(false);
  const [recherche, setRecherche] = useState('');
  const principalRef = useRef(null);
  const premierRendu = useRef(true);

  const cle = adminKeyFromPathname(location.pathname);

  // Derniere section visitee : memorisee ici, relue par l'index (/admin).
  useEffect(() => { if (cle) memoriserDerniereSection(cle); }, [cle]);

  // Titre de l'onglet = section courante ; le focus passe au contenu apres chaque navigation (clavier et lecteurs d'ecran).
  useEffect(() => {
    document.title = `${cle ? t(`nav.items.${cle}`) : t('brand')} · Bookipix Admin`;
    setMenuOuvert(false);
    if (premierRendu.current) { premierRendu.current = false; return; }
    if (principalRef.current) principalRef.current.focus({ preventScroll: false });
  }, [cle, location.pathname, t]);

  // Echap ferme le tiroir du menu (mobile).
  useEffect(() => {
    if (!menuOuvert) return undefined;
    const surTouche = (e) => { if (e.key === 'Escape') setMenuOuvert(false); };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [menuOuvert]);

  const compte = (alerte) => {
    if (alerte === 'failedOrders') return alertes.failedOrders;
    if (alerte === 'incident') return alertes.incident ? 1 : 0;
    if (alerte === 'newVisits') return alertes.visits ? alertes.visits.visitors : 0;
    return 0;
  };

  const chercher = (evenement) => {
    evenement.preventDefault();
    const terme = recherche.trim();
    // Un numéro de commande (CMD-…) mène à la liste des commandes ; tout le reste cherche parmi les livres.
    const section = /^cmd-/i.test(terme) ? 'orders' : 'books';
    navigate(terme ? `${adminPath(section)}?q=${encodeURIComponent(terme)}` : adminPath('books'));
  };

  const pastilleMode = mode === 'production'
    ? { classe: 'is-prod', texte: t('header.modeProduction') }
    : mode === 'test'
      ? { classe: 'is-test', texte: t('header.modeTest') }
      : { classe: 'is-unknown', texte: t('header.modeUnknown') };

  return (
    <div className={`adm${menuOuvert ? ' nav-open' : ''}`}>
      <a className="adm-skip" href="#adm-main">{t('nav.skip')}</a>
      <div className="adm-app">
        <aside className="adm-side" id="adm-side">
          <div className="adm-logo">Book<i>i</i>pix<small>{t('brand')}</small></div>
          <nav aria-label={t('nav.label')} className="adm-nav">
            {ADMIN_GROUPS.map((groupe) => (
              <div className="adm-grp" key={groupe.key}>
                <span className="adm-grp-title">{t(`nav.groups.${groupe.key}`)}</span>
                {groupe.items.map((item) => {
                  const n = compte(item.alert);
                  return (
                    <NavLink key={item.key} to={`${ADMIN_BASE}/${item.path}`} className="adm-nv">
                      <AdminIcon name={item.icon} />
                      <span>{t(`nav.items.${item.key}`)}</span>
                      {n > 0 && (item.alert === 'newVisits'
                        ? <em className="adm-badge is-info" aria-label={t('nav.visitsBadge', { count: n })}>{n}</em>
                        : <em className="adm-badge" aria-label={t('nav.alertBadge', { count: n })}>{n}</em>)}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>
          <div className="adm-me">
            <Link to="/dashboard" className="adm-back">{t('nav.backToSite')}</Link>
          </div>
        </aside>
        <button type="button" className="adm-scrim" aria-label={t('nav.close')} tabIndex={-1} onClick={() => setMenuOuvert(false)} />

        <div className="adm-main-col">
          <header className="adm-top">
            <button
              type="button"
              className="adm-menu-btn"
              aria-label={menuOuvert ? t('nav.close') : t('nav.open')}
              aria-expanded={menuOuvert}
              aria-controls="adm-side"
              onClick={() => setMenuOuvert((v) => !v)}
            >
              <AdminIcon name={menuOuvert ? 'x' : 'menu'} />
            </button>
            <form className="adm-search" role="search" onSubmit={chercher}>
              <AdminIcon name="search" />
              <label className="adm-sr" htmlFor="adm-gsearch">{t('header.searchLabel')}</label>
              <input
                id="adm-gsearch"
                className="adm-input"
                type="search"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder={t('header.searchPlaceholder')}
              />
            </form>
            <span className="adm-sp" />
            <Link to={adminPath('mode')} className={`adm-modepill ${pastilleMode.classe}`} title={t('header.modeTitle')}>
              <i aria-hidden="true" />{pastilleMode.texte}
            </Link>
          </header>
          <main className="adm-work" id="adm-main" tabIndex={-1} ref={principalRef}>
            <Outlet context={{ alertes, mode, marquerTraficVu }} />
          </main>
        </div>
      </div>
    </div>
  );
}

// /admin (sans section) : on rouvre la derniere section visitee, sinon le tableau de bord.
export function AdminIndexRedirect() {
  return <Navigate to={adminPath(lireDerniereSection() || DEFAULT_ADMIN_KEY)} replace />;
}

export default function AdminLayout() {
  return <Gate>{() => <Shell />}</Gate>;
}
